import { surfacingConfig, turfConfig, MINIMUM_SQFT } from '../constants.js';
import { PIP_MATERIALS_VARIANTS, PIP_LABOR_VARIANTS, TURF_MATERIALS_VARIANTS, TURF_LABOR_VARIANTS } from '../assets/generated/constants-snippet-surfacing';
import { pickVariant } from '../utils.js';

const gid = (variantId) => `gid://shopify/ProductVariant/${variantId}`;

// function pickVariant(table, targetPrice) {
//   return (
//     table.find((v) => v.price >= targetPrice) ??
//     table[table.length - 1]
//   );
// }

/**
 * calculateSurfacing.js
 *
 * Calculates Pour-In-Place (PIP) and/or Artificial Turf surfacing costs and
 * returns the Shopify materials + labor line items to append to the draft order.
 *
 * Mirrors _mechanic/calculate_pip.liquid. Include or exclude from the primary
 * server flow by importing or removing the call.
 *
 * Usage (server.js):
 *   import { calculateSurfacing } from './calculateSurfacing.js';
 *
 *   const surfacingItems = calculateSurfacing({
 *     surfacingType:      surfacing_type,         // 'Pour in Place Surfacing' | 'Artificial Turf'
 *     surfacingRequest:   surfacing_request,       // 'Yes' | 'No'
 *     criticalFallHeight: Number(critical_fall_height),
 *     squareFootage:      Number(surfacing_square_footage),
 *   });
 *   // surfacingItems is an array of 0, 1, or 2 line items
 *   lineItems.push(...surfacingItems);
 *
 * Returns an array of line items (materials + labor), each:
 *   { variantId, quantity, customAttributes, appliedDiscount? }
 */


// PIP
const CUSTOM_PIP_MATERIALS_VARIANT_ID = surfacingConfig.custom_pip_materials_variant_id;
const CUSTOM_PIP_LABOR_VARIANT_ID     = surfacingConfig.custom_pip_labor_variant_id;

/** Cost/sq ft from `surfacingConfig.surfacing_tiers` (`p_${feet}_ft`). */
function pipCostPerSqft(fallHeight) {
  return surfacingConfig.surfacing_tiers[`p_${fallHeight}_ft`];
}

// Turf
const CUSTOM_TURF_MATERIALS_VARIANT_ID = turfConfig.turf_custom_materials_variant_id;
const CUSTOM_TURF_LABOR_VARIANT_ID     = turfConfig.turf_custom_labor_variant_id;




// ── Helpers ───────────────────────────────────────────────────────────────────

/** Normalize a product ID to its numeric string, stripping any GID prefix. */
function normalizeProductId(id) {
  if (!id) return '';
  return String(id).replace(/^gid:\/\/shopify\/Product\//i, '');
}

/**
 * Find an item in the items array by numeric product ID.
 * Handles both bare numeric IDs and full GIDs on item.productId / item.product_id.
 */
function findItemByProductId(items, numericId) {
  const want = String(numericId);
  return (items ?? []).find((item) => {
    const raw = item.productId ?? item.product_id ?? item.product?.id ?? '';
    return normalizeProductId(raw) === want;
  });
}

/** Options rows from quote line items (some flows duplicate these on `properties`). */
function lineItemOptionRows(item) {
  if (!item) return [];
  const a = item.options;
  const b = item.properties;
  const rows = [];
  if (Array.isArray(a)) rows.push(...a);
  if (Array.isArray(b) && b !== a) rows.push(...b);
  return rows;
}

/**
 * Extract an option value from an item by matching the option's name/key/label
 * (case-insensitive, space↔underscore flexible).
 */
function getOptionValue(item, optionKey) {
  if (!item) return null;
  const key = optionKey.toLowerCase().replace(/\s+/g, '_');
  const match = lineItemOptionRows(item).find((o) => {
    const k = (o.name ?? o.key ?? o.label ?? '').toLowerCase().replace(/\s+/g, '_');
    return k === key;
  });
  return match?.value ?? null;
}

/**
 * Parse a value that may include a unit suffix to a plain number.
 * e.g. "4 Feet" → 4, "500" → 500, 6 → 6
 */
function parseNumeric(value) {
  if (value == null) return NaN;
  return parseFloat(String(value).replace(/[^0-9.]/g, ''));
}

/** First numeric value > 0 from raw option string/form field; else 0. */
function positiveParsed(raw) {
  const n = parseNumeric(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Pick pip vs turf when calculator products and/or form surfacing type disagree.
 */
function resolveSurfacingKind({
  hasPIPProduct,
  hasTurfProduct,
  formWantsPip,
  formWantsTurf,
}) {
  if (hasPIPProduct && !hasTurfProduct) return 'pip';
  if (hasTurfProduct && !hasPIPProduct) return 'turf';
  if (hasPIPProduct && hasTurfProduct) {
    if (formWantsPip && !formWantsTurf) return 'pip';
    if (formWantsTurf && !formWantsPip) return 'turf';
    return 'pip';
  }
  if (formWantsPip && !formWantsTurf) return 'pip';
  if (formWantsTurf && !formWantsPip) return 'turf';
  if (formWantsPip && formWantsTurf) return 'pip';
  return null;
}

/**
 * Build customAttributes from a source item's options.
 * - Uses the human-readable label as the key (falls back to name/key).
 * - Skips options whose key/label starts with underscore.
 * - Replaces the square_footage value with sqftLabel when the minimum is enforced.
 */
function buildItemCustomAttrs(item, { sqftLabel } = {}) {
  return lineItemOptionRows(item)
    .filter((o) => {
      const displayKey = o.label ?? o.name ?? o.key ?? '';
      return !displayKey.startsWith('_');
    })
    .map((o) => {
      const displayKey = o.label ?? o.name ?? o.key;
      const internalKey = (o.name ?? o.key ?? '').toLowerCase().replace(/\s+/g, '_');
      // Substitute the minimum-adjusted sqft label if provided
      if (internalKey === 'square_footage' && sqftLabel != null) {
        return { key: displayKey, value: sqftLabel };
      }
      return { key: displayKey, value: o.value };
    });
}

/**
 * Extract "selected variant options" from an item's display title.
 * We take the text after the last " - " and split by " / ".
 *
 * Example:
 *  "Pour-In-Place Playground Surfacing Fully Installed - Curbed (Concrete Existing) / Black"
 *    -> ["Curbed (Concrete Existing)", "Black"]
 */
function selectedOptionsFromTitle(title) {
  const t = String(title ?? '').trim();
  if (!t) return [];

  const idx = t.lastIndexOf(' - ');
  if (idx === -1) return [];

  const tail = t.slice(idx + 3).trim();
  if (!tail) return [];

  return tail.split(' / ').map((s) => s.trim()).filter(Boolean);
}


function variantOptionAttrsFromItem(item, kind) {
  const clean = (v) => {
    const s = String(v ?? '').trim();
    if (!s || s.toLowerCase() === 'default title') return null;
    if (s.startsWith('_')) return null;
    return s;
  };

  // In many cart payloads we get Shopify variant options as option1/option2/option3.
  // For surfacing calculators these map nicely:
  // - PIP: option1=edge terminations, option2=color
  // - Turf: option1=color
  const o1 = clean(item?.option1);
  const o2 = clean(item?.option2);

  if (kind === 'pip') {
    const attrs = [];
    if (o1) attrs.push({ key: 'Edge Terminations', value: o1 });
    if (o2) attrs.push({ key: 'Color', value: o2 });
    return attrs;
  }

  if (kind === 'turf') {
    if (o1) return [{ key: 'Color', value: o1 }];
    return [];
  }

  return [];
}

function dedupeAttrs(attrs) {
  const seen = new Set();
  const out = [];
  for (const a of attrs ?? []) {
    const key = String(a?.key ?? '').trim();
    const value = String(a?.value ?? '').trim();
    if (!key || !value) continue;
    const sig = `${key}::${value}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push({ key, value });
  }
  return out;
}



function buildLineItem({ calculatedPrice, variantTable, customAttrs }) {

  const selected      = pickVariant(variantTable, calculatedPrice);
  const discountAmt   = Math.round(Math.max(0, selected.price - calculatedPrice));

  const lineItem = {
    variantId:        gid(selected.variantId),
    quantity:         1,
    customAttributes: [
      { key: 'Quote', value: 'Surfacing' },
      ...customAttrs,
    ],
  };

  if (discountAmt > 0) {
    lineItem.appliedDiscount = {
      valueType: 'FIXED_AMOUNT',
      title:     `Surfacing - Discount`,
      value:     discountAmt,
    };
  }

  return lineItem;
}

// ── PIP calculation ───────────────────────────────────────────────────────────

function calcPIP({ sqft, fallHeight, originalSqft, extraAttrs = [] }) {
  const costPerSqft = pipCostPerSqft(fallHeight);
  if (!costPerSqft) return null; // unsupported fall height → caller sets custom

  const totalCost      = costPerSqft * sqft;
  const totalPrice     = totalCost * surfacingConfig.p_margin;
  const materialsCost  = totalCost  * surfacingConfig.surfacing_mats_perc;
  const laborCost      = totalCost  - materialsCost;
  const materialsPrice = materialsCost * surfacingConfig.p_margin;
  const laborPrice     = laborCost     * surfacingConfig.p_margin;

  // Use item's own options as custom attrs when available (extraAttrs), otherwise
  // fall back to bare key/value pairs.
  const sharedAttrs = extraAttrs.length > 0
    ? extraAttrs
    : [
        { key: 'Critical Fall Height', value: String(fallHeight) },
        { key: 'Square Footage',       value: originalSqft < MINIMUM_SQFT ? `${sqft} (500 Sq Ft Minimum)` : String(sqft) },
      ];

  return {
    materialsLineItem: buildLineItem({
      calculatedPrice: Math.round(materialsPrice),
      variantTable:    PIP_MATERIALS_VARIANTS,
      customAttrs:     sharedAttrs,
    }),
    laborLineItem: buildLineItem({
      calculatedPrice: Math.round(laborPrice),
      variantTable:    PIP_LABOR_VARIANTS,
      customAttrs:     sharedAttrs,
    }),
    totalCost,
    totalPrice,
  };
}

// ── Turf calculation ──────────────────────────────────────────────────────────

function getTurfCostPerSqft(sqft, fallHeight) {
  const fh = Number(fallHeight);
  const area = Number(sqft);
  if (!Number.isFinite(fh) || !Number.isFinite(area)) return null;

  const cfg = turfConfig?.t_pri_config;
  const rows =
    (cfg && typeof cfg === 'object' && !Array.isArray(cfg))
      ? (fh <= 5 ? cfg.t_5_ft : fh <= 10 ? cfg.t_10_ft : null) : null;

  if (!Array.isArray(rows) || rows.length === 0) return null;

  for (const row of rows) {
    const minSqft = Number(row?.[0]);
    const cost = Number(row?.[1]);
    if (!Number.isFinite(minSqft) || !Number.isFinite(cost)) continue;
    if (area >= minSqft) return cost;
  }

  return null;
}

function calcTurf({ sqft, fallHeight, originalSqft, extraAttrs = [] }) {
  const costPerSqft = getTurfCostPerSqft(sqft, fallHeight);
  if (!costPerSqft) return null;

  const totalCost      = costPerSqft * sqft;
  const totalPrice     = totalCost * turfConfig.t_margin;
  const materialsCost  = totalCost  * turfConfig.turf_mats_perc;
  const laborCost      = totalCost  - materialsCost;
  const materialsPrice = materialsCost * turfConfig.t_margin;
  const laborPrice     = laborCost     * turfConfig.t_margin;

  const sharedAttrs = extraAttrs.length > 0
    ? extraAttrs
    : [
        { key: 'Critical Fall Height', value: String(fallHeight) },
        { key: 'Square Footage',       value: originalSqft < MINIMUM_SQFT ? `${sqft} (500 Sq Ft Minimum)` : String(sqft) },
      ];

  return {
    materialsLineItem: buildLineItem({
      calculatedPrice: Math.round(materialsPrice),
      variantTable:    TURF_MATERIALS_VARIANTS,
      customAttrs:     sharedAttrs,
    }),
    laborLineItem: buildLineItem({
      calculatedPrice: Math.round(laborPrice),
      variantTable:    TURF_LABOR_VARIANTS,
      customAttrs:     sharedAttrs,
    }),
    totalCost,
    totalPrice,
  };
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Calculate surfacing line items (materials + labor) for PIP and/or Turf.
 *
 * Priority for criticalFallHeight / squareFootage:
 *   1. Options on the PIP product (id 7167726583905) or Turf product (id 7431093813345)
 *      if that product appears in `items`.
 *   2. The criticalFallHeight / squareFootage params (from formData) as fallback.
 *
 * @param {object}  opts
 * @param {Array}   [opts.items]             - Full line-items array from the cart/quote.
 *                                             Used to detect PIP/Turf calculator products.
 * @param {string}  opts.surfacingType       - 'Pour in Place Surfacing' | 'Artificial Turf'
 * @param {string}  opts.surfacingRequest    - 'Yes' | 'No' | '' (skip if 'No')
 * @param {number}  opts.criticalFallHeight  - Fallback critical fall height in feet
 * @param {number}  opts.squareFootage       - Fallback area in sq ft (enforced minimum: 500)
 *
 * @returns {Array} Array of 0–2 line items: [materialsItem, laborItem]
 */
function calculateSurfacingDetailsInternal({
  items            = [],
  surfacingType    = '',
  surfacingRequest = '',
  criticalFallHeight,
  squareFootage,
} = {}) {
  // ── Detect PIP / Turf calculator products in the items array ─────────────
  const pipItem  = findItemByProductId(items, surfacingConfig.surfacing_product_id);
  const turfItem = findItemByProductId(items, turfConfig.turf_product_id);

  const hasPIPProduct = !!pipItem;
  const hasTurfProduct = !!turfItem;
  const formWantsPip = String(surfacingType).includes('Pour in Place');
  const formWantsTurf = String(surfacingType).includes('Artificial Turf');
  const mentionsPipOrTurf = hasPIPProduct || hasTurfProduct || formWantsPip || formWantsTurf;

  if (!mentionsPipOrTurf) {
    return { kind: null, lineItems: [], totalCost: null, totalPrice: null };
  }

  // Skip if customer explicitly said No AND no calculator product is in the cart
  if (!hasPIPProduct && !hasTurfProduct && String(surfacingRequest).toLowerCase() !== 'yes') {
    return { kind: null, lineItems: [], totalCost: null, totalPrice: null };
  }

  const kind = resolveSurfacingKind({
    hasPIPProduct,
    hasTurfProduct,
    formWantsPip,
    formWantsTurf,
  });
  if (!kind) {
    return { kind: null, lineItems: [], totalCost: null, totalPrice: null };
  }

  // ── Resolve fall height and square footage ────────────────────────────────
  // Prefer positive numeric values from the active calculator line's options; then form params.
  const sourceItem = kind === 'pip' ? pipItem : turfItem;

  const optFh = sourceItem ? getOptionValue(sourceItem, 'critical_fall_height') : null;
  const optSq = sourceItem ? getOptionValue(sourceItem, 'square_footage') : null;
  const optSubBase = sourceItem ? getOptionValue(sourceItem, 'sub_base_details') : null;

  const fallHeight = positiveParsed(optFh) || positiveParsed(criticalFallHeight) || 0;
  const originalSqft = positiveParsed(optSq) || positiveParsed(squareFootage) || 0;
  const subBaseDetails = optSubBase || null;
  // Apply minimum square footage
  const sqft = Math.max(originalSqft, MINIMUM_SQFT);
  const sqftLabel = originalSqft > 0 && originalSqft < MINIMUM_SQFT ? `${sqft} (500 Sq Ft Minimum)` : String(originalSqft || sqft);

  // ── Build shared custom attributes ────────────────────────────────────────
  const extraAttrs = sourceItem ? buildItemCustomAttrs(sourceItem, { sqftLabel }) : [];
  const explicitVariantAttrs = sourceItem ? variantOptionAttrsFromItem(sourceItem, kind) : [];
  const sharedAttrs = dedupeAttrs([...extraAttrs, ...explicitVariantAttrs]);

  if (fallHeight === 0 || originalSqft === 0) {
    // Can't calculate without both dimensions — return custom line items
    const incompleteAttrs = [
      { key: 'INCOMPLETE', value: 'Missing fall height or square footage' },
      ...sharedAttrs,
    ];
    if (kind === 'pip') {
      return {
        kind,
        totalCost: null,
        totalPrice: null,
        lineItems: [
        { variantId: gid(CUSTOM_PIP_MATERIALS_VARIANT_ID), quantity: 1, customAttributes: incompleteAttrs },
        { variantId: gid(CUSTOM_PIP_LABOR_VARIANT_ID),     quantity: 1, customAttributes: incompleteAttrs },
        ],
      };
    }
    return {
      kind,
      totalCost: null,
      totalPrice: null,
      lineItems: [
        { variantId: gid(CUSTOM_TURF_MATERIALS_VARIANT_ID), quantity: 1, customAttributes: incompleteAttrs },
        { variantId: gid(CUSTOM_TURF_LABOR_VARIANT_ID),     quantity: 1, customAttributes: incompleteAttrs },
      ],
    };
  }

  if (kind === 'pip') {
    const result = calcPIP({ sqft, fallHeight, originalSqft, extraAttrs: sharedAttrs });

    if (!result) {
      // Unsupported fall height → custom
      const customAttrs = [
        { key: 'INCOMPLETE', value: 'Needs Custom Quote' },
        ...(sharedAttrs.length ? sharedAttrs : [
          { key: 'Critical Fall Height', value: String(fallHeight) },
          { key: 'Square Footage',        value: sqftLabel },
        ]),
      ];
      return {
        kind,
        totalCost: null,
        totalPrice: null,
        lineItems: [
          { variantId: gid(CUSTOM_PIP_MATERIALS_VARIANT_ID), quantity: 1, customAttributes: customAttrs },
          { variantId: gid(CUSTOM_PIP_LABOR_VARIANT_ID),     quantity: 1, customAttributes: customAttrs },
        ],
      };
    }

    return {
      kind,
      totalCost: result.totalCost,
      totalPrice: result.totalPrice,
      lineItems: [result.materialsLineItem, result.laborLineItem],
    };
  }

  // Turf
  const result = calcTurf({ sqft, fallHeight, originalSqft, extraAttrs: sharedAttrs });

  if (!result) {
    const customAttrs = [
      { key: 'INCOMPLETE', value: 'Needs Custom Quote' },
      ...(sharedAttrs.length ? sharedAttrs : [
        { key: 'Critical Fall Height', value: String(fallHeight) },
        { key: 'Square Footage',        value: sqftLabel },
      ]),
    ];
    return {
      kind,
      totalCost: null,
      totalPrice: null,
      lineItems: [
        { variantId: gid(CUSTOM_TURF_MATERIALS_VARIANT_ID), quantity: 1, customAttributes: customAttrs },
        { variantId: gid(CUSTOM_TURF_LABOR_VARIANT_ID),     quantity: 1, customAttributes: customAttrs },
      ],
    };
  }

  return {
    kind,
    totalCost: result.totalCost,
    totalPrice: result.totalPrice,
    lineItems: [result.materialsLineItem, result.laborLineItem],
  };
}

/**
 * Client/server-friendly surfacing calculator returning BOTH derived line items
 * and totals (for conversion/analytics).
 *
 * @returns {{ kind: 'pip'|'turf'|null, lineItems: Array, totalCost: number|null, totalPrice: number|null }}
 */
export function calculateSurfacingDetails(opts = {}) {
  return calculateSurfacingDetailsInternal(opts);
}

/**
 * Backwards-compatible helper: returns only the surfacing line items.
 * @returns {Array} Array of 0–2 line items: [materialsItem, laborItem]
 */
export function calculateSurfacing(opts = {}) {
  return calculateSurfacingDetailsInternal(opts).lineItems ?? [];
}
